import React, { useMemo, useState } from 'react';

import { usePatchModuleMutation } from 'src/redux/services/centralApi';
import { Button, Checkbox, Flex, Form, Input, Modal } from 'antd';
import type { Module } from 'src/interfaces/entities/module';
import { EditOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import SelectTeam from 'src/pages/observers/module/components/SelectTeam';
import SelectTarget from 'src/pages/observers/module/components/SelectTarget';
import SelectIcon from 'src/pages/observers/module/components/SelectIcon';

interface IEditModuleForm extends Omit<Module, 'module_id' | 'metadata'> {
	metadata: { key: string, value: string }[]
}

export default function EditModule(
	{
		row,
	}: { row: Module }
) {
	const [ open, setOpen, ] = useState<boolean>(false);
	const [ patchModule, patchModuleResult, ] = usePatchModuleMutation();

	const [ editModuleForm, ] = Form.useForm<IEditModuleForm>();

	const initialMetadata = useMemo(() => {
		if(!row.metadata) return [];

		return Object.entries(row.metadata).map(([ key, value, ]) => ({
			key: key,
			value: value,
		}));
	}, [ row.metadata, ]);

	const onFinishEditModule = (module: IEditModuleForm) => {
		const metadata: Record<string, string> = {};
		module.metadata.forEach(({ key, value, }) => {
			metadata[key] = value;
		});

		patchModule({
			id: row.module_id,
			body: {
				...module,
				metadata,
			},
		})
			.unwrap()
			.then(() => {
				toast.success('Módulo editado com sucesso!');
			})
			.catch(() => {
				toast.error('Erro ao editar módulo!');
			})
			.finally(() => {
				setOpen(false);
			});
	};

	return <>
		<Button
			type="text"
			icon={<EditOutlined />}
			onClick={() => setOpen(true)}
		/>
		<Modal
			centered
			title={'Editar módulo'}
			open={open}
			onCancel={() => {
				setOpen(false);
				toast.info('Operação cancelada!', { autoClose: 1000, });
			}}
			destroyOnHidden
			confirmLoading={patchModuleResult.isLoading}
			okText="Salvar"
			onOk={() => {
				editModuleForm.submit();
			}}
		>
			<Form
				layout="vertical"
				onFinish={onFinishEditModule}
				form={editModuleForm}
				style={{ marginTop: 10, }}
			>
				<Form.Item
					initialValue={row.name}
					name={'name'}
					hasFeedback
					required
					label={'Nome do módulo'}
					rules={[{
						required: true,
						message: 'Campo obrigatório.',
					},]}
				>
					<Input placeholder="Nome do módulo" />
				</Form.Item>
				<Form.Item
					initialValue={row.icon}
					name={'icon'}
					hasFeedback
					tooltip={'Ícone de identificação do módulo'}
					label={'Ícone'}
					rules={[{
						required: true,
						message: 'Campo obrigatório.',
					},]}
				>
					<SelectIcon
						defaultIcon={row.icon}
						onIconChange={(value) =>
							editModuleForm.setFieldValue('icon', value)
						}
					/>
				</Form.Item>
				<Form.Item
					initialValue={row.short_description}
					name={'short_description'}
					hasFeedback
					tooltip={'Uma breve descrição exibida no preview do módulo.'}
					label={'Breve descrição'}
					help={'Máximo de caracteres: 128'}
					rules={[
						{
							required: true,
							message: 'Campo obrigatório.',
						},
						{
							max: 128,
							message: 'Máximo de caracteres: 128',
						},
					]}
				>
					<Input maxLength={128} placeholder="Breve descrição" />
				</Form.Item>
				<Form.Item
					initialValue={row.long_description}
					name={'long_description'}
					hasFeedback
					tooltip={'Uma descrição exibida ao abrir o módulo.'}
					label={'Descrição detalhada'}
					rules={[{
						required: true,
						message: 'Campo obrigatório.',
					},]}
				>
					<Input.TextArea placeholder="Descrição detalhada" />
				</Form.Item>
				<Form.Item
					initialValue={row.team}
					name={'team'}
					hasFeedback
					tooltip={'Informe o time que desenvolverá os observadores do módulo.'}
					label={'Time'}
					rules={[{
						required: true,
						message: 'Campo obrigatório.',
					},]}
				>
					<SelectTeam />
				</Form.Item>
				<Form.Item
					initialValue={row.target}
					name={'target'}
					hasFeedback
					tooltip={'Informe o ERP dos observadores do módulo.'}
					label={'ERP'}
					rules={[{
						required: true,
						message: 'Campo obrigatório.',
					},]}
				>
					<SelectTarget />
				</Form.Item>
				<Form.List
					name="metadata"
					initialValue={initialMetadata}
				>
					{(fields, { add, remove, }) => (
						<>
							{fields.map(({ key, name, ...restField }) => (
								<Flex
									gap={8}
									key={key}
									style={{ marginBottom: 8, }}
									align="center"
								>
									<Form.Item
										{...restField}
										name={[ name, 'key', ]}
										rules={[{
											required: true,
											message: 'Campo obrigatório.',
										},]}
									>
										<Input placeholder="Campo" />
									</Form.Item>
									<Form.Item
										{...restField}
										name={[ name, 'value', ]}
										rules={[{
											required: true,
											message: 'Campo obrigatório.',
										},]}
										style={{ width: '100%', }}
									>
										<Input placeholder="Valor" />
									</Form.Item>
									<MinusCircleOutlined
										style={{ marginBottom: '24px', }}
										onClick={() => remove(name)}
									/>
								</Flex>
							))}
							<Form.Item>
								<Button
									type="dashed"
									onClick={() => add()}
									block
									icon={<PlusOutlined />}
								>
								Novo campo personalizado
								</Button>
							</Form.Item>
						</>
					)}
				</Form.List>
				<hr />
				<Form.Item
					initialValue={row.is_active}
					name='is_active'
					valuePropName={'checked'}
					hasFeedback
				>
					<Checkbox>Módulo ativo</Checkbox>
				</Form.Item>
			</Form>
		</Modal>
	</>;
}